/**
 * <s-video-coverflow> — centre-focused video carousel.
 *
 * The card nearest the centre of [data-vc-track] gets [data-active]: its video
 * plays muted, every other uploaded video pauses; TikTok players load when the
 * carousel scrolls into view. Tapping a
 * side card centres it; --prev / --next commands step one card. Playback pauses
 * while the carousel is off screen. With reduced motion nothing autoplays; tap
 * the centred video to play it.
 */
class SVideoCoverflow extends HTMLElement {
  #ac;
  #track;
  #cards = [];
  #active = null;
  #frame = 0;
  #visible = false;
  #observer;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  connectedCallback() {
    this.#track = this.querySelector('[data-vc-track]');
    if (!this.#track) return;
    this.#cards = [...this.#track.querySelectorAll('[data-vc-card]')];
    if (!this.#cards.length) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#track.addEventListener('scroll', this.#queueUpdate, { ...opts, passive: true });
    window.addEventListener('resize', this.#queueUpdate, { ...opts, passive: true });
    this.addEventListener('command', this.#onCommand, opts);
    this.#track.addEventListener('click', this.#onClick, opts);

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#syncPlayback();
    }, { threshold: 0.35 });
    this.#observer.observe(this);

    // Start on the second card when there are three or more, so both sides show.
    const start = this.#cards.length > 2 ? this.#cards[1] : this.#cards[0];
    requestAnimationFrame(() => {
      this.#center(start, 'instant');
      this.#update();
    });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #queueUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const trackBox = this.#track.getBoundingClientRect();
    const middle = trackBox.left + trackBox.width / 2;
    let nearest = this.#cards[0];
    let nearestDistance = Infinity;

    for (const card of this.#cards) {
      const box = card.getBoundingClientRect();
      const distance = Math.abs(box.left + box.width / 2 - middle);
      if (distance < nearestDistance) {
        nearest = card;
        nearestDistance = distance;
      }
    }

    if (nearest === this.#active) return;
    this.#active?.removeAttribute('data-active');
    this.#active = nearest;
    nearest.setAttribute('data-active', '');
    this.#syncPlayback();
  }

  #syncPlayback() {
    for (const card of this.#cards) {
      const video = card.querySelector('video');
      const isActive = card === this.#active && this.#visible;
      // Like the live theme, every TikTok player loads once the carousel is on screen.
      if (card.dataset.kind === 'tiktok') {
        // Side TikToks show their real thumbnail; the player loads in the centre.
        if (this.#visible) this.#loadThumbnail(card);
        if (isActive) this.#loadTikTok(card);
        continue;
      }
      if (card.dataset.kind === 'facebook') {
        // The Facebook player loads once the card reaches the centre.
        if (isActive) this.#loadFacebook(card);
        continue;
      }
      if (!video) continue;
      if (isActive && !this.#reducedMotion) {
        video.play().catch(() => {});
      } else if (!isActive) {
        video.pause();
      }
      card.toggleAttribute('data-playing', isActive && !video.paused);
      video.onplay = () => card.setAttribute('data-playing', '');
      video.onpause = () => card.removeAttribute('data-playing');
    }
  }

  /** Swaps the product-photo cover for the TikTok video's own thumbnail (public oEmbed). */
  async #loadThumbnail(card) {
    if (card.dataset.thumb) return;
    card.dataset.thumb = 'loading';
    const url = card.dataset.tiktokUrl;
    const holder = card.querySelector('[data-vc-embed]');
    if (!url || !holder) return;
    try {
      const response = await fetch(`https://www.tiktok.com/oembed?url=${encodeURIComponent(url)}`);
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      if (!data.thumbnail_url) throw new Error('no thumbnail');
      holder.style.backgroundImage = `url("${data.thumbnail_url.replace(/"/g, '%22')}")`;
      holder.style.backgroundSize = 'cover';
      holder.style.backgroundPosition = 'center';
      if (!holder.querySelector('iframe')) holder.querySelector('img')?.remove();
      card.dataset.thumb = 'done';
    } catch {
      card.dataset.thumb = 'failed';
    }
  }

  #loadFacebook(card) {
    const holder = card.querySelector('[data-vc-embed]');
    const url = card.dataset.fbUrl;
    if (!holder || !url || holder.querySelector('iframe')) return;

    const src = new URL('https://www.facebook.com/plugins/video.php');
    src.searchParams.set('href', url);
    src.searchParams.set('show_text', 'false');
    src.searchParams.set('autoplay', this.#reducedMotion ? 'false' : 'true');
    src.searchParams.set('mute', '1');

    const frame = document.createElement('iframe');
    frame.title = this.dataset.facebookLabel || 'Facebook video';
    frame.allow = 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture; web-share';
    frame.allowFullscreen = true;
    frame.scrolling = 'no';
    frame.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0;display:block;overflow:hidden;';
    frame.src = src.toString();
    holder.replaceChildren(frame);
    card.setAttribute('data-embedded', '');
  }

  #loadTikTok(card) {
    const holder = card.querySelector('[data-vc-embed]');
    const id = card.dataset.tiktokId;
    if (!holder || !id || holder.querySelector('iframe')) return;

    const src = new URL(`https://www.tiktok.com/player/v1/${encodeURIComponent(id)}`);
    src.searchParams.set('autoplay', this.#reducedMotion ? '0' : '1');
    src.searchParams.set('muted', '1');
    src.searchParams.set('loop', '1');
    src.searchParams.set('controls', '1');
    src.searchParams.set('description', '0');
    src.searchParams.set('music_info', '0');

    // Same player setup as the live theme. The iframe replaces the product-photo
    // cover (which would otherwise sit on top of it) and fills the card.
    const frame = document.createElement('iframe');
    frame.title = this.dataset.tiktokLabel || 'TikTok video';
    frame.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
    frame.allowFullscreen = true;
    frame.referrerPolicy = 'strict-origin-when-cross-origin';
    frame.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0;display:block;';
    frame.src = src.toString();
    holder.replaceChildren(frame);
    card.setAttribute('data-embedded', '');
  }

  #onClick = (event) => {
    const sound = event.target.closest('[data-vc-sound]');
    const card = event.target.closest('[data-vc-card]');
    if (!card) return;

    if (sound) {
      const video = card.querySelector('video');
      if (!video) return;
      video.muted = !video.muted;
      if (!video.muted) video.play().catch(() => {});
      sound.setAttribute('aria-pressed', String(!video.muted));
      sound.querySelector('[data-vc-wave]')?.classList.toggle('hidden', video.muted);
      sound.querySelector('[data-vc-mute]')?.classList.toggle('hidden', !video.muted);
      return;
    }

    // Links and buttons inside the card (Shop now) keep their own behaviour.
    if (event.target.closest('a, button, form')) return;

    if (card !== this.#active) {
      this.#center(card);
      return;
    }

    const video = card.querySelector('video');
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  };

  #onCommand = (event) => {
    const index = this.#cards.indexOf(this.#active);
    if (event.command === '--prev') this.#center(this.#cards[Math.max(0, index - 1)]);
    if (event.command === '--next') this.#center(this.#cards[Math.min(this.#cards.length - 1, index + 1)]);
  };

  #center(card, behavior = 'smooth') {
    if (!card) return;
    const trackBox = this.#track.getBoundingClientRect();
    const box = card.getBoundingClientRect();
    const offset = box.left + box.width / 2 - (trackBox.left + trackBox.width / 2);
    this.#track.scrollBy({ left: offset, behavior: this.#reducedMotion ? 'instant' : behavior });
  }
}

if (!customElements.get('s-video-coverflow')) {
  customElements.define('s-video-coverflow', SVideoCoverflow);
}
